// ============================================================================
// JARVISTRAVEL - RECEIPT MATCH CARD
// Version: 1.0.0
// Confirm or skip a receipt-to-itinerary match. Salvaged from the retired
// ReceiptSection and restyled to semantic tokens + lucide (no emoji). Reused by
// the Journal review flow and, later, inline in the Jarvis conversation.
// ============================================================================

import { useState } from 'react';
import { Target, Check } from 'lucide-react';
import { Button, Badge } from '@/app/components/shared';
import { confirmReceipt, dismissReceipt } from '@/app/api/receipts';
import type { ReceiptMatchResult } from '@/app/api/receipts';
import { isDemoSession } from '@/app/api/client';

interface ReceiptMatchCardProps {
  tripId: string;
  match: ReceiptMatchResult;
  onConfirmed?: (itemId: string) => void;
  onDismissed?: () => void;
}

const CONFIDENCE_VARIANT = {
  high: 'success',
  medium: 'warning',
  low: 'danger',
} as const;

export function ReceiptMatchCard({ tripId, match, onConfirmed, onDismissed }: ReceiptMatchCardProps) {
  const [busy, setBusy] = useState(false);
  const [resolved, setResolved] = useState(false);
  const best = match.best_match;

  // Demo has no backend, so confirm/skip are optimistic there — otherwise the
  // showcase flow would stall on a failed network call.
  const handleConfirm = async () => {
    if (!best) return;
    setBusy(true);
    const ok = isDemoSession() ? true : await confirmReceipt(tripId, match.receipt_id, best.itinerary_item_id);
    setBusy(false);
    if (ok) {
      setResolved(true);
      onConfirmed?.(best.itinerary_item_id);
    }
  };

  // As confirm: the card goes only once the skip is recorded. A refused skip
  // (a trip whose expenses have closed, whose own toast says so) used to close
  // the card as if it had worked (JAR-1864).
  const handleDismiss = async () => {
    setBusy(true);
    const ok = isDemoSession() ? true : await dismissReceipt(tripId, match.receipt_id);
    setBusy(false);
    if (ok) {
      setResolved(true);
      onDismissed?.();
    }
  };

  if (resolved) return null;

  if (best) {
    return (
      <div className="rounded-xl border border-border bg-surface-raised p-3">
        <div className="flex items-center gap-2 mb-1.5">
          <Target className="w-4 h-4 text-accent shrink-0" strokeWidth={2} aria-hidden="true" />
          <span className="text-sm font-semibold text-ink">Match found</span>
          <Badge variant={CONFIDENCE_VARIANT[best.confidence]} size="sm">
            <span className="capitalize">{best.confidence}</span>
          </Badge>
        </div>
        <p className="text-sm font-medium text-body">{best.item_title}</p>
        <p className="text-xs text-muted mt-0.5">{best.match_reason}</p>
        <div className="flex gap-2 mt-2.5">
          <Button size="sm" variant="primary" onClick={handleConfirm} loading={busy}>
            <Check className="w-4 h-4" strokeWidth={2.5} aria-hidden="true" /> Confirm
          </Button>
          <Button size="sm" variant="ghost" onClick={handleDismiss} disabled={busy}>
            Skip
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-surface-raised p-3">
      <p className="text-sm font-medium text-body mb-1">No exact match</p>
      {match.suggestions && match.suggestions.length > 0 && (
        <ul className="space-y-1 mb-2">
          {match.suggestions.map((s, i) => (
            <li key={i} className="text-xs text-muted">· {s}</li>
          ))}
        </ul>
      )}
      <Button size="sm" variant="ghost" onClick={handleDismiss} disabled={busy}>
        Dismiss
      </Button>
    </div>
  );
}

export default ReceiptMatchCard;
